import React, { useMemo, useRef, useState } from "react";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import Popper from "@mui/material/Popper";
import Select, { SelectProps } from "@mui/material/Select";
import styled from "styled-components";

import {
    IPickerGroup,
    IPickerTab,
    Picker,
    PickerItem,
} from "@formant/ui/component/picker";

const StyledFormControl = styled(FormControl)`
    width: 100%;
`;

const StyledPopper = styled(Popper)`
    z-index: 1300;
`;

const StyledInputLabel = styled(InputLabel)`
    margin-left: -0.938rem;
`;

const StyledFormHelperText = styled(FormHelperText)`
    margin-left: 0.2rem;
`;

const StyledLabel = styled.div`
    color: var(--primary-white, #fff);
    font-size: 13px;
    line-height: 20px;
`;

interface ICommonGroupPickerProps {
    id: string;
    selectProps?: SelectProps;
    helperTextProps?: { error: boolean; label: string };
    tabs?: IPickerTab[];
    groups: IPickerGroup[];
    options: any[];
    optionLabelKey: string;
    optionIdKey: string;
    onSetSelected: (option: any) => void;
    selectedOption: any;
}

export default function CommonGroupPicker({
    id,
    selectProps,
    helperTextProps,
    tabs,
    groups,
    options,
    optionLabelKey,
    optionIdKey,
    onSetSelected,
    selectedOption,
}: ICommonGroupPickerProps) {
    const listOptions: PickerItem<any, string>[] = useMemo(() => {
        const optionValues = options || [];

        return optionValues.map(optionValue => {
            return {
                group: optionValue.group,
                label: optionValue[optionLabelKey] as string,
                onClick: () => {
                    onSetSelected(optionValue);
                },
            };
        });
    }, [options, optionLabelKey]);

    const items = [...listOptions];
    const anchorRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [selectedTab, setSelectedTab] = useState<string | null | undefined>(
        tabs && tabs?.length > 0 ? tabs[0]?.id : undefined,
    );

    const handleClick = () => {
        setOpen(val => !val);
    };

    return (
        <StyledFormControl>
            {!selectedOption ? (
                <StyledInputLabel shrink={false}>
                    {selectProps?.placeholder}
                </StyledInputLabel>
            ) : null}
            <Select
                ref={anchorRef}
                label="trololo"
                aria-describedby={id}
                onClick={handleClick}
                onKeyDownCapture={ev => {
                    if (ev.key === "Enter") {
                        handleClick();
                        ev.preventDefault();
                    }
                }}
                id={`select-group-with-picker-${selectedOption?.[optionIdKey]}`}
                value={selectedOption?.[optionIdKey] || ""}
                renderValue={() => (
                    <StyledLabel>
                        {selectedOption?.[optionLabelKey]}
                    </StyledLabel>
                )}
                open={false}
                variant="standard"
                {...selectProps}
            />
            <StyledFormHelperText error={helperTextProps?.error}>
                {helperTextProps?.label}
            </StyledFormHelperText>

            <StyledPopper id={id} open={open} anchorEl={anchorRef.current}>
                <ClickAwayListener onClickAway={handleClick}>
                    <Picker
                        onSelectItem={handleClick}
                        searchPlaceholder={selectProps?.placeholder}
                        noAutoSelectFirst={true}
                        selectedTab={selectedTab}
                        onSelectTab={setSelectedTab}
                        tabs={tabs || undefined}
                        groups={groups}
                        items={items}
                        style={{
                            minWidth: "100%",
                            maxWidth: "100%",
                            minHeight: "100%",
                            maxHeight: "35vh",
                        }}
                    />
                </ClickAwayListener>
            </StyledPopper>
        </StyledFormControl>
    );
}
